import React, { useRef, useState } from "react";

import { trimToDecimals, useTranslation } from "utils";

import { CropMixComposerGoalResponse, CropsOverviewTableColumn, CropsOverviewTableColumnConfig, CropsOverviewTableDataType, SortState } from "models";

import { Checkbox, Flex, OverflowEllipsisWithTooltip, Table } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { ScoreCircleChart, ScoreColor } from "pages/app/crop-mix-composer/components/compositionStep/ScoreCircleChart";
import { TableColumnTitle } from "pages/app/crop-mix-composer/components/compositionStep/TableColumnTitle";
import { getCostSymbol, tableSorting } from "pages/app/crop-mix-composer/utils/cropMixComposer";

export const CropsOverviewTable = ({ cropMixComposerResponse }: { cropMixComposerResponse?: CropMixComposerGoalResponse[] }) => {
  const { t } = useTranslation("cropMixComposer");
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { aggregateGoalScores, aggregateMatchScore, currentStep, selectedGoals, weightShareDistribution, selectedCrops, setSelectedCrops, isChangingStep } =
    useCropMixComposerContext();
  const [sortState, setSortState] = useState<SortState>({
    column: CropsOverviewTableColumn.LOCATION_MATCH,
    direction: "desc",
  });

  const handleSort = (column: string) => {
    setSortState((prev) => ({
      column,
      direction: prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const onCheck = (checked: boolean, crop: CropMixComposerGoalResponse) => {
    if (isChangingStep !== null) return;

    setSelectedCrops?.((prev) => (checked ? [...prev, crop] : prev.filter((c) => c.crop.id !== crop.crop.id)));
  };

  const cropsOverviewTableColumnsConfigs: CropsOverviewTableColumnConfig[] = [
    {
      title: t(`page.${CMC_ROUTES.COMPOSITION}.tableHeader.${CropsOverviewTableColumn.COVER_CROP}`),
      dataIndex: CropsOverviewTableColumn.COVER_CROP,
      key: CropsOverviewTableColumn.COVER_CROP,
      className: "crop-name",
    },
    ...(currentStep === CMC_ROUTES.OVERVIEW
      ? [
          {
            title: t(`page.${CMC_ROUTES.COMPOSITION}.tableHeader.${CropsOverviewTableColumn.SEEDING_RATE}`),
            dataIndex: CropsOverviewTableColumn.SEEDING_RATE,
            key: CropsOverviewTableColumn.SEEDING_RATE,
            className: "seeding-rate",
          },
          {
            title: t(`page.${CMC_ROUTES.COMPOSITION}.tableHeader.${CropsOverviewTableColumn.WEIGHT_SHARE}`),
            dataIndex: CropsOverviewTableColumn.WEIGHT_SHARE,
            key: CropsOverviewTableColumn.WEIGHT_SHARE,
            className: "weight-share",
          },
        ]
      : []),
    {
      title: t(`page.${CMC_ROUTES.COMPOSITION}.tableHeader.${CropsOverviewTableColumn.LOCATION_MATCH}`),
      dataIndex: CropsOverviewTableColumn.LOCATION_MATCH,
      key: CropsOverviewTableColumn.LOCATION_MATCH,
      className: "location-match",
    },
    ...selectedGoals.map((goal, idx) => ({
      title: t(`goals.${goal}`),
      dataIndex: `goal${idx + 1}`,
      key: goal,
      className: `goal${idx + 1}`,
    })),
  ];

  const getColumns = (
    cropsOverviewTableColumnsConfigs: CropsOverviewTableColumnConfig[],
    sortState: SortState,
    handleSort: (key: string) => void
  ): CropsOverviewTableColumnConfig[] => {
    const cropColumnWidth = 30;
    const remainingWidth = 100 - cropColumnWidth;
    const otherColumnsCount = cropsOverviewTableColumnsConfigs.length > 1 ? cropsOverviewTableColumnsConfigs.length - 1 : 1;
    const otherColumnWidth = remainingWidth / otherColumnsCount;

    return [
      {
        title: " ",
        dataIndex: "select",
        key: "select",
        width: currentStep === CMC_ROUTES.COMPOSITION ? "3rem" : undefined,
      },
      ...cropsOverviewTableColumnsConfigs.map((col) => ({
        title: (
          <TableColumnTitle
            title={col.title as string}
            columnKey={col.key}
            active={sortState.column === col.key}
            direction={sortState.direction}
            currentStep={currentStep}
            onClick={() => (currentStep === CMC_ROUTES.COMPOSITION ? handleSort(col.key) : null)}
            className={col.className}
          />
        ),
        dataIndex: col.dataIndex,
        key: col.key,
        width: col.key === CropsOverviewTableColumn.COVER_CROP ? `${cropColumnWidth}%` : `${otherColumnWidth}%`,
      })),
    ];
  };

  const tableData: CropsOverviewTableDataType[] =
    cropMixComposerResponse
      ?.sort((a, b) => tableSorting(a, b, sortState, t, selectedGoals))
      ?.map((item, idx) => ({
        key: item.crop.id,
        select:
          currentStep === CMC_ROUTES.COMPOSITION ? (
            <Checkbox
              className="small-checkbox"
              checked={selectedCrops.some((crop: CropMixComposerGoalResponse) => crop.crop.id === item.crop.id)}
              onChange={(e) => {
                e.stopPropagation();
                onCheck?.(e.target.checked, item);
              }}
            />
          ) : null,
        coverCrop: (
          <Flex vertical>
            <Flex align="center" gap="0.5rem">
              <OverflowEllipsisWithTooltip title={item.crop.name} className="crop-name">
                {item.crop.name}
              </OverflowEllipsisWithTooltip>
              {/* {idx < 3 ? <span className="top-fit-label paragraph-xs">{t(`page.${CMC_ROUTES.COMPOSITION}.tableBody.topFit`)}</span> : null} */}
            </Flex>
            <OverflowEllipsisWithTooltip
              title={`${t(`plantFamilies.${item.crop.cropFamily}`)} · ${getCostSymbol(item.crop.coastCategory)}`}
              className="crop-details"
            >
              {t(`plantFamilies.${item.crop.cropFamily}`)} · {getCostSymbol(item.crop.coastCategory)}
            </OverflowEllipsisWithTooltip>
          </Flex>
        ),
        seedingRate: currentStep === CMC_ROUTES.OVERVIEW ? `${weightShareDistribution[item.crop.id].weightSeedingRate} kg/ha` : undefined,
        weightShare: currentStep === CMC_ROUTES.OVERVIEW ? `${trimToDecimals(weightShareDistribution[item.crop.id].weightShare, 1)}%` : undefined,
        locationMatch: (
          <ScoreCircleChart dataTestId={`table-row-${idx}-score-circle-chart-location-match`} score={item.matchScore || 0} color={ScoreColor.BROWN} size={32} />
        ),
        goal1: (
          <ScoreCircleChart
            dataTestId={`table-row-${idx}-score-circle-chart-1`}
            score={item.scores.find((score) => score.level2 === selectedGoals[0])?.score || 0}
            color={ScoreColor.PINK}
            size={32}
          />
        ),
        goal2: (
          <ScoreCircleChart
            dataTestId={`table-row-${idx}-score-circle-chart-2`}
            score={item.scores.find((score) => score.level2 === selectedGoals[1])?.score || 0}
            color={ScoreColor.LIGHT_BLUE}
            size={32}
          />
        ),
        goal3: (
          <ScoreCircleChart
            dataTestId={`table-row-${idx}-score-circle-chart-3`}
            score={item.scores.find((score) => score.level2 === selectedGoals[2])?.score || 0}
            color={ScoreColor.PURPLE}
            size={32}
          />
        ),
      })) || [];

  const generateTotalRow = (selectedCrops: CropMixComposerGoalResponse[]): CropsOverviewTableDataType => {
    const totalSeedingRate = selectedCrops.reduce((sum, crop) => sum + (weightShareDistribution[crop.crop.id].weightSeedingRate || 0), 0);

    const weightedAggregateGoalScores = aggregateGoalScores.map((goal, idx) => (
      <ScoreCircleChart
        key={`total-goal-${idx}`}
        dataTestId={`total-score-circle-chart-${idx + 1}`}
        score={Math.round(goal.score || 0)}
        color={[ScoreColor.PINK, ScoreColor.LIGHT_BLUE, ScoreColor.PURPLE][idx]}
        size={32}
      />
    ));

    return {
      key: "total",
      select: null,
      coverCrop: (
        <Flex vertical>
          <span className="crop-name total-row">{t(`page.${CMC_ROUTES.COMPOSITION}.tableBody.total`)}</span>
        </Flex>
      ),
      seedingRate: `${trimToDecimals(totalSeedingRate)} kg/ha`,
      weightShare: "100%",
      locationMatch: (
        <ScoreCircleChart dataTestId="total-score-circle-chart-location-match" score={aggregateMatchScore || 0} color={ScoreColor.BROWN} size={32} />
      ),
      goal1: weightedAggregateGoalScores[0],
      goal2: weightedAggregateGoalScores[1],
      goal3: weightedAggregateGoalScores[2],
    };
  };

  if (currentStep === CMC_ROUTES.OVERVIEW && cropMixComposerResponse?.length) {
    const selectedCrops = cropMixComposerResponse.filter((crop) => tableData.some((row) => row.key === crop.crop.id));
    tableData.push(generateTotalRow(selectedCrops));
  }

  return (
    <div className={`crop-mix-composer-overlay-step-overview step${currentStep} table-container`} ref={tableContainerRef}>
      <Table
        className="crop-mix-composer-overlay-step-overview table"
        data-testid="crop-mix-composer-overlay-step-overview-table"
        columns={getColumns(cropsOverviewTableColumnsConfigs, sortState, handleSort)}
        data={tableData}
        pagination={false}
        setEqualWidthColumns={false}
        sticky={true}
        scroll={currentStep === CMC_ROUTES.OVERVIEW ? undefined : { y: `calc(100vh - ${tableContainerRef.current?.offsetTop}px - 10rem)` }}
      />
    </div>
  );
};
